import { type Edge, type Node, Position } from '@xyflow/react';

import { SHAPE_ASPECT_RATIO } from '@/components/diagrams/components/DiagramShape/DiagramShape';
import { getTileDefaultWidth } from '@/components/diagrams/components/Tile/Tile.utils';
import {
  type ArrowEdgeRoute,
  type ArrowEdgeStyleVariant,
  type ArrowEdgeVariant,
  DiagramEdgeArrowheads,
  DiagramEdgeRoute,
  DiagramNodeDisplay,
  GraphVizIconShape,
  isDiagramHexColor,
} from '@/components/diagrams/diagramStyle';
import { type ArrowProps } from '@/components/diagrams/FlowChart/edges/ArrowEdge';
import { type TileNodeData } from '@/components/diagrams/FlowChart/nodes/TileNode/TileNode';
import { DiagramNodeKind } from '@/types/schema';

import { type DiagramBuilder_DiagramFragment } from '../graphql/Diagram.generated';
import { type DiagramBuilderNode } from './diagram';
import { POSITION_BY_SIDE } from './diagramConnections';
import { type DiagramTile, getDiagramTile } from './diagramTiles';

export type ReactFlowDiagramNodeData = TileNodeData & {
  item: DiagramBuilderNode;
};
export type ReactFlowDiagramEdgeData = ArrowProps['data'] & {
  label: string;
  /**
   * The saved label position, a fraction (0 to 1) of the line length. When
   * it is not set, the label is at the middle of the line.
   */
  labelFraction?: number;
};
/**
 * The React Flow tiles and arrows of a diagram. The builder uses two names
 * for its two shapes of a diagram:
 *
 * - A "diagram" (`DiagramBuilder_DiagramFragment`) is the document in the
 *   GraphQL schema: the content, with the saved position of each tile. The
 *   edits change it, and the editor saves it.
 * - A "React Flow diagram" (`ReactFlowDiagram`) is the same diagram as React
 *   Flow nodes and edges. It comes from a diagram and is never saved. The
 *   `useReactFlowDiagram` hook and the ELK layout use it.
 */
export type ReactFlowDiagram = {
  nodes: Node<ReactFlowDiagramNodeData, 'tile'>[];
  edges: Edge<ReactFlowDiagramEdgeData, 'arrow'>[];
};
/** A tile of a React Flow diagram. */
export type ReactFlowDiagramNode = ReactFlowDiagram['nodes'][number];
/** An arrow of a React Flow diagram. */
export type ReactFlowDiagramEdge = ReactFlowDiagram['edges'][number];

/** The width of new standalone text, in canvas pixels. Its height fits the text. */
const TEXT_DEFAULT_WIDTH = 200;

/** The width of a node drawn as a shape, in canvas pixels. */
const SHAPE_DEFAULT_WIDTH = 120;

/**
 * Returns the width of a node without a saved width. Standalone text wraps at
 * this width. The height of a node without a saved height fits its content.
 */
function getDefaultNodeWidth(
  item: DiagramBuilderNode,
  display: DiagramTile['componentType']
): number | undefined {
  if (display === DiagramNodeDisplay.Shape) return SHAPE_DEFAULT_WIDTH;
  switch (item.kind) {
    case DiagramNodeKind.Text:
      return TEXT_DEFAULT_WIDTH;
    case DiagramNodeKind.Shape:
    case DiagramNodeKind.Person:
    case DiagramNodeKind.Entity:
    case DiagramNodeKind.Document:
    case DiagramNodeKind.Asset:
    case DiagramNodeKind.Summary:
    case DiagramNodeKind.Payment:
      return getTileDefaultWidth(display === DiagramNodeDisplay.TileIcon);
  }
}

// One canvas pixel keeps a nonzero final segment for SVG arrow orientation
// without the default 20-pixel stubs reversing short connections.
export const BUILDER_EDGE_ENDPOINT_STUB = 1;

/**
 * Convert diagram content to React Flow tiles and arrows, with saved
 * positions. `getDiagramTile` maps the kind, subtype, estate category, and
 * members of each node to a catalogue tile. The diagram flows from top to
 * bottom. The content `direction` does not change the layout. The browser
 * measures the height of a tile without a saved height at the selected width.
 */
export function createReactFlowDiagram(
  diagram: DiagramBuilder_DiagramFragment
): ReactFlowDiagram {
  const content = diagram.content;
  const nodesByID = new Map(content.nodes.map((node) => [node.nodeID, node]));
  return {
    nodes: content.nodes.map((item) => {
      const tile = getDiagramTile(item, nodesByID);
      const width =
        item.nodeStyle?.width ?? getDefaultNodeWidth(item, tile.componentType);
      let height = item.nodeStyle?.height ?? undefined;
      if (tile.componentType === DiagramNodeDisplay.Shape) {
        // Shapes keep their proportions. Old saved heights can differ from
        // the outline, so use its ratio for the frame and resize handles too.
        height =
          (width ?? SHAPE_DEFAULT_WIDTH) *
          SHAPE_ASPECT_RATIO[tile.iconShape ?? GraphVizIconShape.Rounded];
      } else if (item.kind === DiagramNodeKind.Text) {
        height = undefined;
      }
      return {
        id: item.nodeID,
        type: 'tile',
        data: {
          // The tile shows its text as plain text, as the estate waterfall
          // and presentation tiles do. The user writes all of the text, so
          // the tile shows all of it and wraps it at the tile width.
          showFullText: true,
          lineOne: item.label,
          lineTwo: item.subtitle || undefined,
          // A tile setting can hide the notes. The node keeps its text.
          notes: (!item.nodeStyle?.hideDetail && item.detail) || undefined,
          ...tile,
          // The user edits the text in place on the canvas, the styles in the
          // selection bar, and the size with the shared resizer. The tile has
          // no settings modal.
          hideNodeControls: true,
          // The shared tile node draws a tile at its measured width unless the
          // data sets a width. An icon without a set width sizes to its label,
          // so a measured width that is a fraction too small does not wrap it.
          ...(width === undefined && { width: undefined }),
          item,
        },
        position: item.position ?? { x: 0, y: 0 },
        // The shared resizer changes the node width and height, as in the
        // other editors. Without a saved height, the height follows the tile
        // content. A TEXT node always fits its text, so that its measured
        // size includes all of the text. It uses its saved height as its
        // smallest height.
        width,
        height,
        sourcePosition: Position.Bottom,
        targetPosition: Position.Top,
      };
    }),
    edges: content.edges.map((item) => {
      const style = item.edgeStyle;
      // A color is a palette token or a hex color.
      const color = style?.color;
      const customColor = isDiagramHexColor(color) ? color : undefined;
      return {
        id: item.edgeID,
        type: 'arrow',
        source: item.sourceNodeID,
        target: item.targetNodeID,
        // Without a set side, a connection leaves the bottom of its source
        // and enters the top of its target, as the diagram flows downwards.
        sourceHandle: item.sourceSide
          ? POSITION_BY_SIDE[item.sourceSide]
          : Position.Bottom,
        targetHandle: item.targetSide
          ? POSITION_BY_SIDE[item.targetSide]
          : Position.Top,
        data: {
          // A hidden label keeps its text in the diagram, but the line, the
          // layout, and the renderer treat the line as one without a label.
          label: style?.hideLabel ? '' : item.label,
          labelFraction: item.labelPosition ?? undefined,
          variant: ((!customColor && color) || 'primary') as ArrowEdgeVariant,
          customColor,
          style: (style?.lineStyle ?? 'solid') as ArrowEdgeStyleVariant,
          route: (style?.route ?? DiagramEdgeRoute.Elbow) as ArrowEdgeRoute,
          strokeWidth: style?.strokeWidth ?? undefined,
          hideArrow: style?.arrowheads === DiagramEdgeArrowheads.None,
          showFromArrow:
            style?.arrowheads === DiagramEdgeArrowheads.Both ||
            style?.arrowheads === DiagramEdgeArrowheads.Start,
          hideDestinationArrow:
            style?.arrowheads === DiagramEdgeArrowheads.Start,
          waypoints: item.waypoints?.length ? item.waypoints : undefined,
          offset: BUILDER_EDGE_ENDPOINT_STUB,
          borderRadius: 0,
          // ELK routes start and end at the tile borders, so the arrows add
          // no endpoint stubs.
          routedEndpointStubs: { source: 0, target: 0 },
        },
      };
    }),
  };
}

/**
 * Returns true when an ELK route can draw the arrow. ELK routes elbow paths
 * from the bottom of the source to the top of the target. A set side, route
 * shape, or waypoint replaces the ELK route.
 */
export function canUseLayoutRoute(edge: ReactFlowDiagramEdge): boolean {
  return (
    edge.sourceHandle === Position.Bottom &&
    edge.targetHandle === Position.Top &&
    edge.data?.route === DiagramEdgeRoute.Elbow &&
    !edge.data.waypoints
  );
}
